<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useDetectDevice } from '@/composables/device'
import { useNotify } from '@/composables/notify'
import { useRoiComparison } from '@/composables/roi-comparison'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import RoiComparisonView from '@/components/roi/RoiComparisonView.vue'
import RoiComparisonLibrary from '@/components/roi/RoiComparisonLibrary.vue'
import RoiComparisonsGrid from '@/components/roi/RoiComparisonsGrid.vue'
import RoiComparisonEdit from '@/components/roi/RoiComparisonEdit.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'

const i18n = getI18n()
const route = useRoute()
const webSiteStore = useWebSiteStore()
const { roiComparisonEditStepEnum: STEP } = usePlanningStore()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()

const comparison = useRoiComparison()
const gridRef = ref(null)
const editTarget = ref(null)
const showDetail = ref(false)
const toDelete = ref(null)
const showDeleteDialog = ref(false)

const openAsync = async(layout, items = null) => {
  await comparison.openAsync(layout, items)
  if (isMobile.value) showDetail.value = true
  else gridRef.value.expand(layout.id)
}

const openEdit = (layout, options = {}) => { editTarget.value = { layout, ...options } }
const onAdd = (quick) => openEdit({}, { quick })
const onEdit = (layout) => openEdit(layout, { items: layout.id === comparison.current?.id ? comparison.items : null })
const onClone = (layout) => openEdit(
  { name: i18n.t('roiCompare.copyOf', { name: layout.name }), data: { ...layout.data } },
  { items: layout.id === comparison.current?.id ? [...comparison.items] : null, initialStep: STEP.ITEMS },
)

const onSaved = async(saved, items) => {
  const isNew = !editTarget.value.layout.id
  editTarget.value = null
  notifySuccess(i18n.t(isNew ? 'info.createdSuccessfully' : 'info.updatedSuccessfully'))
  await comparison.fetchSavedAsync()
  if (isNew || comparison.current?.id === saved.id) await openAsync(saved, items)
}

const onDeleted = async(layout) => {
  editTarget.value = null
  if (comparison.current?.id === layout.id) showDetail.value = false
  await comparison.deleteSavedAsync(layout)
  notifySuccess(i18n.t('info.deletedSuccessfully'))
}

const onDeleteRequested = (layout) => {
  toDelete.value = layout
  showDeleteDialog.value = true
}

const onDetailHide = () => {
  showDetail.value = false
  comparison.close()
}

onMounted(async() => {
  if (isMobile.value) webSiteStore.fabActions = [{ id: 'roi-add', icon: 'mdi-chart-line', onClick: () => onAdd(false) }]
  await comparison.fetchSavedAsync()
  const requested = comparison.savedComparisons.find(layout => String(layout.id) === route.query.id)
  if (requested) await openAsync(requested)
})

onUnmounted(() => {
  webSiteStore.fabActions = []
})
</script>

<template>
  <div>
    <roi-comparison-library
      v-if="isMobile"
      class="q-pt-sm"
      :comparison="comparison"
      @open="openAsync"
      @edit="onEdit"
      @clone="onClone"
      @delete="onDeleteRequested"
    />
    <roi-comparisons-grid
      v-else
      ref="gridRef"
      :comparison="comparison"
      @open="comparison.openAsync"
      @edit="onEdit"
      @clone="onClone"
      @add="onAdd"
      @delete="onDeleteRequested"
    />

    <q-dialog :model-value="showDetail" maximized @hide="onDetailHide">
      <q-card v-if="comparison.current" class="dialog-card-flex">
        <navigate-back :text="comparison.current.name" @cancel="onDetailHide">
          <template #right>
            <q-btn
              v-if="webSiteStore.canManageOwned(comparison.current, webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)"
              flat
              round
              icon="mdi-pencil"
              @click="onEdit(comparison.current)"
            />
          </template>
        </navigate-back>
        <q-card-section class="col column no-wrap overflow-hidden q-pa-none">
          <roi-comparison-view :comparison="comparison" />
        </q-card-section>
      </q-card>
    </q-dialog>

    <q-dialog
      :model-value="editTarget !== null"
      :maximized="isMobile"
      class="dialog-xs"
      @hide="editTarget = null"
    >
      <roi-comparison-edit
        v-if="editTarget"
        :model-value="editTarget.layout"
        :items="editTarget.items"
        :initial-step="editTarget.initialStep"
        :initial-quick-create="editTarget.quick"
        @update="onSaved"
        @cancel="editTarget = null"
        @delete="onDeleted"
      />
    </q-dialog>

    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDeleteMessage', { item: toDelete?.name })"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleted(toDelete)"
    />
  </div>
</template>
